import React, { useState, useEffect, useCallback } from "react";
// Removed direct axios import, using api instead
import {
  IconEdit as FiEdit2,
  IconTrash as FiTrash2,
  IconPlus as FiPlus,
  IconX as FiX,
  IconSearch,
  IconRefresh,
  IconLayoutGrid,
  IconList,
  IconCheck,
  IconLayoutDashboard,
  IconVideo,
  IconBook,
  IconNews,
  IconExternalLink,
  IconUpload,
  IconChevronRight,
  IconSettings,
  IconLogout,
  IconChevronUp,
  IconChevronDown,
} from "@tabler/icons-react";

import api, { API_URL, formatFileUrl } from "../../utils/api";

const API_BASE = `${API_URL}/library`;

function extractVideoId(url) {
  if (!url) return null;
  const regex =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

export const AdminLibrary = () => {
  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    window.location.reload();
  };

  const [activeTab, setActiveTab] = useState("all");

  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [colSearchTerm, setColSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  // Modals
  const [isCatModalOpen, setCatModalOpen] = useState(false);
  const [isItemModalOpen, setItemModalOpen] = useState(false);

  // Forms
  const [catForm, setCatForm] = useState({
    title: "",
    subtitle: "",
    coverImage: "",
    mainUrl: "",
    type: "book",
  });
  const [itemForm, setItemForm] = useState({
    title: "",
    day: "",
    url: "",
    pdf: "",
    coverImage: "",
    month: "",
  });
  const [editCatId, setEditCatId] = useState(null);
  const [editItemId, setEditItemId] = useState(null);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    text: "",
    onConfirm: null,
  });

  // Articles State
  const [articles, setArticles] = useState([]);
  const [selectedArticleCategory, setSelectedArticleCategory] = useState("All");
  const [articleSearchTerm, setArticleSearchTerm] = useState("");
  const [isArticleModalOpen, setArticleModalOpen] = useState(false);
  const [editArticleId, setEditArticleId] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    slug: "",
    arabicTitle: "",
    category: "Hadith Science",
    author: "",
    authorRole: "",
    authorImage: "",
    date: "",
    readTime: "5 min read",
    image: "",
    excerpt: "",
    content: [{ type: "paragraph", text: "" }],
  });

  const showNotification = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchCategories = async (type) => {
    setIsRefreshing(true);
    try {
      const url =
        type === "all"
          ? `${API_URL}/library/categories`
          : `${API_URL}/library/categories?type=${type}`;
      const res = await api.get(url);
      setCategories(Array.isArray(res.data) ? res.data : []);

      if (type === "all") {
        const itemsRes = await api.get(`${API_URL}/library/items`);
        setAllItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);

        try {
          const articlesRes = await api.get(`${API_URL}/articles`);
          setArticles(Array.isArray(articlesRes.data) ? articlesRes.data : []);
        } catch (e) {
          console.error("Failed to load articles for dashboard:", e);
        }
      }
    } catch (err) {
      console.error(err);
      showNotification("Failed to fetch data", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchItems = useCallback(async (catId) => {
    try {
      const res = await api.get(`${API_BASE}/items?category=${catId}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification("Failed to fetch items", "error");
    }
  }, []);

  // Interceptor logic moved to api.js

  useEffect(() => {
    if (activeTab === "article") {
      fetchArticles();
      fetchCategories("article");
    } else {
      fetchCategories(activeTab);
    }

    setSelectedCategory(null);
    setItems([]);
  }, [activeTab]);

  useEffect(() => {
    if (selectedCategory) {
      fetchItems(selectedCategory._id);
    }
  }, [selectedCategory, fetchItems]);

  const handleUpload = async (e, field, targetType) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      showNotification("Uploading...", "info");
      const uploadUrl = targetType === "article" ? `${API_URL}/articles/upload` : `${API_BASE}/upload`;
      const res = await api.post(uploadUrl, formData);
      if (targetType === "item") {
        setItemForm((prev) => ({ ...prev, [field]: res.data.url }));
      } else if (targetType === "article") {
        setArticleForm((prev) => ({ ...prev, [field]: res.data.url }));
      } else {
        setCatForm((prev) => ({ ...prev, [field]: res.data.url }));
      }
      showNotification("File uploaded successfully");
    } catch (err) {
      console.error(err);
      showNotification("Upload failed", "error");
    }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const finalType = editCatId
        ? catForm.type
        : activeTab === "all"
          ? catForm.type
          : activeTab;
      if (editCatId) {
        await api.put(`${API_BASE}/categories/${editCatId}`, {
          ...catForm,
          type: finalType,
        });
        showNotification("Collection updated");
      } else {
        await api.post(`${API_BASE}/categories`, {
          ...catForm,
          type: finalType,
        });
        showNotification("Collection created");
      }
      setCatModalOpen(false);
      setEditCatId(null);
      fetchCategories(activeTab);
    } catch (err) {
      console.error(err);
      showNotification("Failed to save collection", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteCategory = (id) => {
    setConfirmModal({
      isOpen: true,
      text:
        activeTab === "article"
          ? "Are you sure you want to delete this category? Articles in this category will remain, but will be uncategorized."
          : "Delete this collection? All resources inside will be permanently removed.",
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, text: "", onConfirm: null });
        try {
          await api.delete(`${API_BASE}/categories/${id}`);
          if (selectedCategory?._id === id) setSelectedCategory(null);
          fetchCategories(activeTab);
          showNotification(
            activeTab === "article" ? "Category removed" : "Collection removed",
          );
        } catch (err) {
          console.error(err);
          showNotification(
            activeTab === "article"
              ? "Error deleting category"
              : "Error deleting collection",
            "error",
          );
        }
      },
    });
  };

  const saveItem = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editItemId) {
        await api.put(`${API_BASE}/items/${editItemId}`, itemForm);
        showNotification("Resource updated");
      } else {
        await api.post(`${API_BASE}/items`, {
          ...itemForm,
          category: selectedCategory._id,
        });
        showNotification("Resource created");
      }
      setItemModalOpen(false);
      setEditItemId(null);
      fetchItems(selectedCategory._id);
    } catch (err) {
      console.error(err);
      showNotification("Failed to save resource", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteItem = (id) => {
    setConfirmModal({
      isOpen: true,
      text: "Are you sure you want to delete this resource?",
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, text: "", onConfirm: null });
        try {
          await api.delete(`${API_BASE}/items/${id}`);
          fetchItems(selectedCategory._id);
          showNotification("Resource removed");
        } catch (err) {
          console.error(err);
          showNotification("Error removing resource", "error");
        }
      },
    });
  };

  // Articles Admin Functions
  const fetchArticles = async () => {
    setIsRefreshing(true);
    try {
      const res = await api.get(`${API_URL}/articles`);
      setArticles(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      showNotification("Failed to fetch articles", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  const saveArticle = async (e) => {
    if (e) e.preventDefault();
    if (!articleForm.title.trim()) {
      showNotification("Article Title is required", "error");
      return;
    }
    setIsSaving(true);
    try {
      let finalSlug = articleForm.slug.trim();
      if (!finalSlug) {
        finalSlug = articleForm.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)+/g, "");
      }
      const finalForm = { ...articleForm, slug: finalSlug };

      if (editArticleId) {
        await api.put(`${API_URL}/articles/${editArticleId}`, finalForm);
        showNotification("Article updated successfully");
      } else {
        await api.post(`${API_URL}/articles`, finalForm);
        showNotification("Article created successfully");
      }
      setArticleModalOpen(false);
      setEditArticleId(null);
      fetchArticles();
    } catch (err) {
      console.error(err);
      showNotification("Failed to save article", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteArticle = (id) => {
    setConfirmModal({
      isOpen: true,
      text: "Are you sure you want to delete this article? This action cannot be undone.",
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, text: "", onConfirm: null });
        try {
          await api.delete(`${API_URL}/articles/${id}`);
          fetchArticles();
          showNotification("Article deleted successfully");
        } catch (err) {
          console.error(err);
          showNotification("Error deleting article", "error");
        }
      },
    });
  };

  const openArticleNew = () => {
    setEditArticleId(null);
    setArticleForm({
      title: "",
      slug: "",
      arabicTitle: "",
      category:
        selectedArticleCategory === "All"
          ? categories[0]?.title || "Hadith Science"
          : selectedArticleCategory,
      author: "Darul Hadees Scholar",
      authorRole: "Islamic Researcher",
      authorImage: "",
      date: new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      readTime: "5 min read",
      image: "",
      excerpt: "",
      content: [{ type: "paragraph", text: "" }],
    });
    if (categories.length === 0 || categories[0]?.type !== "article") {
      fetchCategories("article");
    }
    setArticleModalOpen(true);
  };

  const openArticleEdit = (art) => {
    setEditArticleId(art._id || art.id);
    setArticleForm({
      title: art.title || "",
      slug: art.slug || art.id || "",
      arabicTitle: art.arabicTitle || "",
      category: art.category || categories[0]?.title || "Hadith Science",
      author: art.author || "",
      authorRole: art.authorRole || "",
      authorImage: art.authorImage || "",
      date: art.date || "",
      readTime: art.readTime || "5 min read",
      image: art.image || "",
      excerpt: art.excerpt || "",
      content:
        art.content && art.content.length > 0
          ? art.content.map((b) => ({ type: b.type, text: b.text }))
          : [{ type: "paragraph", text: "" }],
    });
    if (categories.length === 0 || categories[0]?.type !== "article") {
      fetchCategories("article");
    }
    setArticleModalOpen(true);
  };

  const handleAddArticleBlock = (type = "paragraph") => {
    setArticleForm((prev) => ({
      ...prev,
      content: [...prev.content, { type, text: "" }],
    }));
  };

  const handleRemoveArticleBlock = (index) => {
    if (articleForm.content.length <= 1) {
      showNotification(
        "Articles must have at least one content block",
        "error",
      );
      return;
    }
    setArticleForm((prev) => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index),
    }));
  };

  const handleArticleBlockChange = (index, value) => {
    const updated = [...articleForm.content];
    updated[index].text = value;
    setArticleForm((prev) => ({ ...prev, content: updated }));
  };

  const handleArticleBlockTypeChange = (index, type) => {
    const updated = [...articleForm.content];
    updated[index].type = type;
    setArticleForm((prev) => ({ ...prev, content: updated }));
  };

  const handleMoveArticleBlock = (index, direction) => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === articleForm.content.length - 1)
      return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    const updated = [...articleForm.content];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    setArticleForm((prev) => ({ ...prev, content: updated }));
  };

  const handleUploadArticleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      showNotification("Uploading cover...", "info");
      const res = await api.post(`${API_URL}/articles/upload`, formData);
      setArticleForm((prev) => ({ ...prev, image: res.data.url }));
      showNotification("Cover uploaded successfully");
    } catch (err) {
      console.error(err);
      showNotification("Cover upload failed", "error");
    }
  };

  const handleUploadBlockImage = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      showNotification("Uploading block image...", "info");
      const res = await api.post(`${API_URL}/articles/upload`, formData);
      setArticleForm((prev) => {
        const nextContent = [...prev.content];
        nextContent[index] = { ...nextContent[index], text: res.data.url };
        return { ...prev, content: nextContent };
      });
      showNotification("Block image uploaded successfully");
    } catch (err) {
      console.error(err);
      showNotification("Block image upload failed", "error");
    }
  };

  const openCatEdit = (cat) => {
    setCatForm(cat);
    setEditCatId(cat._id);
    setCatModalOpen(true);
  };

  const openCatNew = () => {
    setCatForm({
      title: "",
      subtitle: "",
      coverImage: "",
      mainUrl: "",
      type: activeTab === "all" ? "book" : activeTab,
    });
    setEditCatId(null);
    setCatModalOpen(true);
  };

  const openItemEdit = (item) => {
    setItemForm(item);
    setEditItemId(item._id);
    setItemModalOpen(true);
  };

  const openItemNew = () => {
    setItemForm({
      title: "",
      day: "",
      url: "",
      pdf: "",
      coverImage: "",
      month: "",
    });
    setEditItemId(null);
    setItemModalOpen(true);
  };

  const getItemImage = (item) => {
    if (item.coverImage) {
      return formatFileUrl(item.coverImage);
    }

    // Find category to check type
    const cat =
      categories.find((c) => c._id === item.category) || selectedCategory;
    if (item.url || (cat && cat.type === "video")) {
      const vidId = extractVideoId(item.url || (cat && cat.mainUrl));
      if (vidId) return `https://img.youtube.com/vi/${vidId}/mqdefault.jpg`;
    }

    return "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg";
  };

  const getCategoryImage = (cat) => {
    if (cat.coverImage) return formatFileUrl(cat.coverImage);
    if (cat.type === "video" && cat.mainUrl) {
      const vidId = extractVideoId(cat.mainUrl);
      if (vidId) return `https://img.youtube.com/vi/${vidId}/mqdefault.jpg`;
    }
    return "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg";
  };

  const filteredItems = items.filter((item) => {
    const query = searchTerm.toLowerCase();
    return (
      (item.title || "").toLowerCase().includes(query) ||
      (item.day || "").toLowerCase().includes(query) ||
      (item.month || "").toLowerCase().includes(query)
    );
  });

  const filteredCategories = categories.filter((cat) => {
    const query = colSearchTerm.toLowerCase();
    return (
      (cat.title || "").toLowerCase().includes(query) ||
      (cat.subtitle || "").toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-8 pb-12 px-4 md:px-8 font-sans selection:bg-pumpkinToast-100 selection:text-pumpkinToast-900 max-w-full overflow-x-hidden">
      {/* Notification Bar */}
      {notification && (
        <div
          className={`fixed top-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 rounded-2xl shadow-2xl flex items-center space-x-3 transition-all transform animate-in fade-in slide-in-from-top-4 ${
            notification.type === "success"
              ? "bg-neutral-900 text-white"
              : notification.type === "error"
                ? "bg-red-600 text-white"
                : "bg-blue-600 text-white"
          }`}
        >
          <IconCheck
            size={18}
            className={
              notification.type === "success"
                ? "text-pumpkinToast-300"
                : "text-white"
            }
          />
          <span className="text-sm font-medium tracking-tight">
            {notification.msg}
          </span>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-pumpkinToast-50 text-pumpkinToast-600 text-[10px] font-bold uppercase rounded-md tracking-wider border border-pumpkinToast-100">
                Admin Dashboard
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-medium text-neutral-900 tracking-tight">
              Library <span className="text-neutral-400">Management</span>
            </h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-md">
              Create, organize and publish your digital assets with a clean and
              efficient workspace.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-6 py-3 bg-red-50 text-red-600 rounded-2xl font-medium border border-red-100 hover:bg-red-100 transition-all shadow-sm"
            >
              <span className="text-sm">Logout</span>
              <IconLogout size={16} />
            </button>
            <a
              href="/library"
              target="_blank"
              rel="noreferrer"
              className="group flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-2xl font-medium border border-neutral-200 hover:bg-neutral-800 transition-all shadow-sm"
            >
              <span className="text-sm">Visit Library</span>
              <IconExternalLink
                size={16}
                className="text-neutral-400 group-hover:text-neutral-200 transition-colors"
              />
            </a>
            <button
              onClick={() =>
                activeTab === "article"
                  ? fetchArticles()
                  : fetchCategories(activeTab)
              }
              disabled={isRefreshing}
              className={`p-3 rounded-2xl bg-white border border-neutral-200 text-neutral-500 hover:border-neutral-900 hover:text-neutral-900 transition-all shadow-sm ${isRefreshing ? "opacity-50" : ""}`}
            >
              <IconRefresh
                size={20}
                className={isRefreshing ? "animate-spin" : ""}
              />
            </button>
          </div>
        </div>

        {/* Tabbed Navigation */}
        <div className="flex overflow-x-auto items-center gap-2 mb-8 bg-neutral-100/60 p-1.5 rounded-[22px] w-fit max-w-full scrollbar-hide border border-neutral-200/50">
          {[
            {
              id: "all",
              label: "Dashboard",
              icon: <IconLayoutDashboard size={18} />,
            },
            { id: "video", label: "Playlists", icon: <IconVideo size={18} /> },
            { id: "book", label: "Books", icon: <IconBook size={18} /> },
            {
              id: "magazine",
              label: "Magazines",
              icon: <IconNews size={18} />,
            },
            { id: "article", label: "Articles", icon: <IconNews size={18} /> },
            { id: "gallery", label: "Gallery", icon: <IconLayoutGrid size={18} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === "gallery") {
                  window.location.href = `/${process.env.REACT_APP_ADMIN_ROUTE || 'admin-library'}/gallery`;
                } else {
                  setActiveTab(tab.id);
                }
              }}
              className={`px-6 py-2.5 rounded-2xl font-medium transition-all flex items-center space-x-2 text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-white text-neutral-900 shadow-md ring-1 ring-black/5"
                  : "text-neutral-500 hover:text-neutral-800 hover:bg-white/50"
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Main Workspace */}
        <div className="min-h-[600px]">
          {activeTab === "all" ? (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                {[
                  {
                    label: "Total Content",
                    value: allItems.length,
                    icon: <IconLayoutGrid size={24} />,
                    color: "text-neutral-900 bg-neutral-100",
                  },
                  {
                    label: "Active Categories",
                    value: categories.length,
                    icon: <IconSettings size={24} />,
                    color: "text-pumpkinToast-600 bg-pumpkinToast-50",
                  },
                  {
                    label: "Video Lessons",
                    value: allItems.filter(
                      (i) =>
                        (i.category?.type ||
                          categories.find(
                            (c) => c._id === (i.category?._id || i.category),
                          )?.type) === "video",
                    ).length,
                    icon: <IconVideo size={24} />,
                    color: "text-blue-600 bg-blue-50",
                  },
                  {
                    label: "Printed Media",
                    value: allItems.filter(
                      (i) =>
                        (i.category?.type ||
                          categories.find(
                            (c) => c._id === (i.category?._id || i.category),
                          )?.type) !== "video",
                    ).length,
                    icon: <IconBook size={24} />,
                    color: "text-green-600 bg-green-100",
                  },
                  {
                    label: "Articles Published",
                    value: articles.length,
                    icon: <IconNews size={24} />,
                    color: "text-pumpkinToast-600 bg-pumpkinToast-50",
                  },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-6 rounded-3xl border border-neutral-200/80 shadow-sm hover:shadow-md transition-all group overflow-hidden relative"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className={`p-3 rounded-2xl ${stat.color} transition-transform group-hover:rotate-12`}
                      >
                        {stat.icon}
                      </div>
                      <IconChevronRight
                        size={16}
                        className="text-neutral-300 group-hover:text-neutral-400 group-hover:translate-x-1 transition-all"
                      />
                    </div>
                    <div>
                      <p className="text-[11px] uppercase font-semibold text-neutral-400 tracking-wider mb-1">
                        {stat.label}
                      </p>
                      <p className="text-3xl font-medium text-neutral-900">
                        {stat.value}
                      </p>
                    </div>
                    <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-neutral-50 rounded-full opacity-50 transition-all group-hover:scale-110"></div>
                  </div>
                ))}
              </div>

              {/* Global Registry Table */}
              <div className="bg-white rounded-3xl border border-neutral-200/80 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-neutral-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-neutral-950 text-white rounded-xl flex items-center justify-center">
                      <IconList size={20} />
                    </div>
                    <h3 className="font-medium text-neutral-800 text-lg">
                      Master Inventory
                    </h3>
                  </div>
                  <div className="relative">
                    <IconSearch
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Search items by title..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-11 pr-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm w-full sm:w-80 focus:bg-white focus:border-neutral-900 outline-none transition-all text-neutral-900 shadow-inner"
                    />
                  </div>
                </div>

                <div className="p-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {allItems
                      .filter((i) => {
                        const q = searchQuery.toLowerCase();
                        const catTitle = (
                          i.category?.title ||
                          categories.find(
                            (c) => c._id === (i.category?._id || i.category),
                          )?.title ||
                          ""
                        ).toLowerCase();
                        return (
                          (i.title || "").toLowerCase().includes(q) ||
                          catTitle.includes(q) ||
                          (i.day || "").toLowerCase().includes(q) ||
                          (i.month || "").toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 60)
                      .map((item) => {
                        const cat = item.category?.title
                          ? item.category
                          : categories.find(
                              (c) =>
                                c._id === (item.category?._id || item.category),
                            );
                        return (
                          <div
                            key={item._id}
                            className="flex items-center gap-4 p-4 rounded-2xl hover:bg-neutral-50 transition-all group"
                          >
                            <div className="w-14 h-14 rounded-[20px] overflow-hidden bg-neutral-100 flex-shrink-0 border border-neutral-200/50 shadow-sm">
                              <img
                                src={getItemImage(item)}
                                alt=""
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                onError={(e) =>
                                  (e.target.src =
                                    "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg")
                                }
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 truncate mb-0.5">
                                {item.title}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded-lg border border-neutral-200 group-hover:bg-neutral-200 transition-colors uppercase tracking-tight">
                                  {cat?.title || "Draft"}
                                </span>
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${cat?.type === "video" ? "bg-blue-400" : "bg-green-400"}`}
                                ></span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    {allItems.length === 0 && (
                      <div className="col-span-full py-32 text-center">
                        <div className="w-20 h-20 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                          <IconLayoutGrid
                            size={32}
                            className="text-neutral-200"
                          />
                        </div>
                        <p className="text-neutral-400 font-medium">
                          Your library is empty. Select a tab to start adding
                          content.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === "article" ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
              {/* Sidebar: Categories */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white rounded-[32px] border border-neutral-200/80 overflow-hidden flex flex-col h-[400px] lg:h-[650px] shadow-sm">
                  <div className="p-6 border-b border-neutral-100 flex flex-col gap-4 bg-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-sm font-semibold text-neutral-900 uppercase tracking-[0.1em] opacity-50">
                          Article Categories
                        </h2>
                        <p className="text-[10px] text-neutral-400 mt-0.5">
                          Filter by scientific branch
                        </p>
                      </div>
                      <button
                        onClick={openCatNew}
                        className="w-10 h-10 bg-neutral-905 bg-black text-white rounded-2xl hover:bg-neutral-800 transition-all shadow-md active:scale-95 flex items-center justify-center"
                        title="New Category"
                      >
                        <FiPlus size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-2 scrollbar-hide">
                    <div
                      className={`p-4 rounded-2xl border transition-all cursor-pointer text-sm font-medium ${
                        selectedArticleCategory === "All"
                          ? "border-pumpkinToast-500 bg-pumpkinToast-500/10 shadow-inner text-pumpkinToast-500"
                          : "border-neutral-100 bg-white hover:border-neutral-300 hover:shadow-sm"
                      }`}
                      onClick={() => setSelectedArticleCategory("All")}
                    >
                      All
                    </div>
                    {categories.map((cat) => (
                      <div
                        key={cat._id}
                        className={`group relative p-4 rounded-2xl border transition-all cursor-pointer text-sm font-medium flex justify-between items-center ${
                          selectedArticleCategory === cat.title
                            ? "border-pumpkinToast-500 bg-pumpkinToast-500/10 shadow-inner text-pumpkinToast-500"
                            : "border-neutral-100 bg-white hover:border-neutral-300 hover:shadow-sm"
                        }`}
                        onClick={() => setSelectedArticleCategory(cat.title)}
                      >
                        <span className="truncate pr-8">{cat.title}</span>
                        <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity absolute right-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openCatEdit(cat);
                            }}
                            className="p-1.5 text-neutral-400 hover:text-neutral-900 transition-colors rounded-xl hover:bg-white/80"
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCategory(cat._id);
                            }}
                            className="p-1.5 text-neutral-400 hover:text-red-500 transition-colors rounded-xl hover:bg-white/80"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Explorer: Articles List */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-[32px] border border-neutral-200/80 flex flex-col h-[500px] lg:h-[650px] shadow-sm relative overflow-hidden">
                  <div className="flex flex-col h-full">
                    <div className="p-6 border-b border-neutral-100 flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white z-10">
                      <div>
                        <h2 className="text-xl font-medium text-neutral-950 uppercase tracking-tight">
                          {selectedArticleCategory} Articles
                        </h2>
                        <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-[0.2em] mt-1">
                          Manage articles
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-auto">
                          <IconSearch
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400"
                            size={16}
                          />
                          <input
                            type="text"
                            placeholder="Quick search articles..."
                            value={articleSearchTerm}
                            onChange={(e) =>
                              setArticleSearchTerm(e.target.value)
                            }
                            className="pl-11 pr-5 py-2.5 bg-neutral-50 border border-neutral-100 rounded-2xl text-[13px] outline-none focus:bg-white focus:border-neutral-900 transition-all w-full sm:w-60 shadow-inner"
                          />
                        </div>
                        <button
                          onClick={openArticleNew}
                          className="flex items-center justify-center gap-2 px-6 py-3 bg-pumpkinToast-500 text-black rounded-2xl hover:bg-neutral-800 transition-all shadow-md active:scale-95 group font-medium text-sm w-full sm:w-auto"
                        >
                          <FiPlus
                            size={18}
                            className="group-hover:rotate-90 transition-transform"
                          />
                          <span>Add Article</span>
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 scrollbar-hide bg-[#FAFAFA]/50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {articles
                          .filter((art) => {
                            const matchesCategory =
                              selectedArticleCategory === "All" ||
                              art.category === selectedArticleCategory;
                            const query = articleSearchTerm.toLowerCase();
                            const matchesSearch =
                              (art.title || "").toLowerCase().includes(query) ||
                              (art.author || "")
                                .toLowerCase()
                                .includes(query) ||
                              (art.excerpt || "").toLowerCase().includes(query);
                            return matchesCategory && matchesSearch;
                          })
                          .map((art) => {
                            const artId = art._id || art.id;
                            const hasImage = art.image && !failedImages[artId];
                            return (
                              <div
                                key={artId}
                                className="flex flex-col bg-white rounded-3xl border border-neutral-200/50 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group ring-4 ring-transparent hover:ring-neutral-950/5"
                              >
                                {hasImage ? (
                                  <div className="relative aspect-video bg-neutral-100">
                                    <img
                                      src={formatFileUrl(art.image)}
                                      alt=""
                                      className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105"
                                      onError={() => {
                                        setFailedImages((prev) => ({ ...prev, [artId]: true }));
                                      }}
                                    />
                                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent translate-y-0 md:translate-y-2 opacity-100 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 transition-all">
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => openArticleEdit(art)}
                                          className="flex-grow py-2 bg-white backdrop-blur-md rounded-xl text-neutral-900 text-[11px] font-bold shadow hover:bg-neutral-100 transition-colors uppercase tracking-wider"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          onClick={() =>
                                            deleteArticle(artId)
                                          }
                                          className="px-3 py-2 bg-red-500 rounded-xl text-white shadow hover:bg-red-600 transition-colors"
                                        >
                                          <FiTrash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 bg-neutral-50 border-b border-neutral-100 flex gap-2">
                                    <button
                                      onClick={() => openArticleEdit(art)}
                                      className="flex-grow py-2 bg-white border border-neutral-200 rounded-xl text-neutral-900 text-[11px] font-bold shadow-sm hover:bg-neutral-50 transition-colors uppercase tracking-wider"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() =>
                                        deleteArticle(artId)
                                      }
                                      className="px-3 py-2 bg-red-500 rounded-xl text-white shadow hover:bg-red-600 transition-colors"
                                    >
                                      <FiTrash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              <div className="p-4 bg-white">
                                <span className="text-[9px] font-bold text-pumpkinToast-600 bg-pumpkinToast-50 px-2 py-0.5 rounded-lg border border-pumpkinToast-100 uppercase tracking-tighter shadow-sm">
                                  {art.category}
                                </span>
                                <h3 className="text-sm font-medium text-neutral-900 line-clamp-2 min-h-[40px] leading-snug mt-2">
                                  {art.title}
                                </h3>
                                <p className="text-[10px] text-neutral-400 mt-2 flex justify-between items-center font-medium">
                                  <span>By {art.author}</span>
                                  <span>{art.readTime || "5 min read"}</span>
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {articles.filter((art) => {
                          const matchesCategory =
                            selectedArticleCategory === "All" ||
                            art.category === selectedArticleCategory;
                          const query = articleSearchTerm.toLowerCase();
                          const matchesSearch =
                            (art.title || "").toLowerCase().includes(query) ||
                            (art.author || "").toLowerCase().includes(query) ||
                            (art.excerpt || "").toLowerCase().includes(query);
                          return matchesCategory && matchesSearch;
                        }).length === 0 && (
                          <div className="col-span-full py-40 text-center">
                            <div className="w-20 h-20 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                              <IconSearch
                                size={28}
                                className="text-neutral-300"
                              />
                            </div>
                            <p className="text-sm text-neutral-400 font-medium">
                              No matching articles found.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
              {/* Collection Side-Bar */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white rounded-[32px] border border-neutral-200/80 overflow-hidden flex flex-col h-[400px] lg:h-[650px] shadow-sm">
                  <div className="p-6 border-b border-neutral-100 flex flex-col gap-4 bg-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-sm font-semibold text-neutral-900 uppercase tracking-[0.1em] opacity-50">
                          Collections
                        </h2>
                        <p className="text-[10px] text-neutral-400 mt-0.5">
                          Categorize your {activeTab}s
                        </p>
                      </div>
                      {!(activeTab === "magazine" && categories.length > 0) && (
                        <button
                          onClick={openCatNew}
                          className="w-10 h-10 bg-black text-white rounded-2xl hover:bg-neutral-800 transition-all shadow-md active:scale-95 flex items-center justify-center"
                          title="New Collection"
                        >
                          <FiPlus size={20} />
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <IconSearch
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                        size={14}
                      />
                      <input
                        type="text"
                        placeholder="Search collections..."
                        value={colSearchTerm}
                        onChange={(e) => setColSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-neutral-50 rounded-xl border border-neutral-100 text-xs focus:bg-white focus:border-neutral-900 outline-none transition-all text-neutral-900 shadow-inner"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide">
                    {filteredCategories.map((cat) => (
                      <div
                        key={cat._id}
                        className={`group relative p-4 rounded-3xl border transition-all cursor-pointer ${
                          selectedCategory?._id === cat._id
                            ? "border-neutral-950 bg-neutral-50 shadow-inner"
                            : "border-neutral-100 bg-white hover:border-neutral-300 hover:shadow-md"
                        }`}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        <div className="flex justify-between items-center gap-4">
                          <div className="w-14 h-14 rounded-2xl overflow-hidden bg-neutral-50 flex-shrink-0 border border-neutral-200/50 shadow-sm relative">
                            <img
                              src={getCategoryImage(cat)}
                              alt=""
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) =>
                                (e.target.src =
                                  "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg")
                              }
                            />
                            {selectedCategory?._id === cat._id && (
                              <div className="absolute inset-0 bg-neutral-900/10 backdrop-blur-[1px] flex items-center justify-center">
                                <IconCheck
                                  size={20}
                                  className="text-white drop-shadow-md"
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium truncate text-neutral-900 mb-0.5 uppercase tracking-tight">
                              {cat.title}
                            </h3>
                            <p className="text-[10px] text-neutral-400 flex items-center gap-1.5 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-neutral-300"></span>
                              {cat.subtitle || "No subtitle"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openCatEdit(cat);
                              }}
                              className="p-2 text-neutral-400 hover:text-neutral-900 transition-colors rounded-xl hover:bg-white"
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteCategory(cat._id);
                              }}
                              className="p-2 text-neutral-400 hover:text-red-500 transition-colors rounded-xl hover:bg-white"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredCategories.length === 0 && (
                      <div className="py-32 text-center">
                        <div className="w-16 h-16 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                          <IconLayoutGrid
                            size={24}
                            className="text-neutral-300"
                          />
                        </div>
                        <p className="text-xs text-neutral-400 font-medium">
                          No collections yet
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Resource Explorer */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-[32px] border border-neutral-200/80 flex flex-col h-[500px] lg:h-[650px] shadow-sm relative overflow-hidden">
                  {!selectedCategory ? (
                    <div className="h-full flex flex-col items-center justify-center text-neutral-400 p-10 text-center bg-[#FAFAFA]">
                      <div className="w-24 h-24 bg-white rounded-full shadow-lg flex items-center justify-center mb-6 ring-8 ring-neutral-50">
                        <IconList size={36} className="text-neutral-900" />
                      </div>
                      <h3 className="text-neutral-900 font-medium text-xl mb-2">
                        Explorer is ready
                      </h3>
                      <p className="text-sm max-w-[280px] leading-relaxed">
                        Select a collection from the sidebar to start managing
                        your resources and content.
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col h-full">
                      <div className="p-6 border-b border-neutral-100 flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white z-10">
                        <div>
                          <h2 className="text-xl font-medium text-neutral-950 uppercase tracking-tight">
                            {selectedCategory.title}
                          </h2>
                          <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-[0.2em] mt-1">
                            Resource Content
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full sm:w-auto">
                          <div className="relative w-full sm:w-auto">
                            <IconSearch
                              className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400"
                              size={16}
                            />
                            <input
                              type="text"
                              placeholder="Quick search items..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="pl-11 pr-5 py-2.5 bg-neutral-50 border border-neutral-100 rounded-2xl text-[13px] outline-none focus:bg-white focus:border-neutral-900 transition-all w-full sm:w-60 shadow-inner"
                            />
                          </div>
                          <button
                            onClick={openItemNew}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-neutral-900 text-black rounded-2xl hover:bg-neutral-800 transition-all shadow-md active:scale-95 group font-medium text-sm w-full sm:w-auto"
                          >
                            <FiPlus
                              size={18}
                              className="group-hover:rotate-90 transition-transform"
                            />
                            <span>Add Resource</span>
                          </button>
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-6 scrollbar-hide bg-[#FAFAFA]/50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                          {filteredItems.map((item) => {
                            const isPortrait =
                              activeTab === "book" || activeTab === "magazine";
                            return (
                              <div
                                key={item._id}
                                className="flex flex-col bg-white rounded-3xl border border-neutral-200/50 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group ring-4 ring-transparent hover:ring-neutral-950/5"
                              >
                                <div
                                  className={`relative ${isPortrait ? "aspect-[3/4]" : "aspect-video"} bg-neutral-100`}
                                >
                                  <img
                                    src={getItemImage(item)}
                                    alt=""
                                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                                    onError={(e) => {
                                      e.target.src =
                                        "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg";
                                      e.target.className =
                                        "w-full h-full object-contain p-4 opacity-50";
                                    }}
                                  />
                                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent translate-y-0 md:translate-y-2 opacity-100 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 transition-all">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => openItemEdit(item)}
                                        className="flex-1 py-1.5 bg-white backdrop-blur-md rounded-xl text-neutral-900 text-[11px] font-bold shadow hover:bg-white transition-colors uppercase tracking-wider"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => deleteItem(item._id)}
                                        className="px-3 py-1.5 bg-red-500 rounded-xl text-white shadow hover:bg-red-600 transition-colors"
                                      >
                                        <FiTrash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="p-4 bg-white">
                                  <h3 className="text-sm font-medium text-neutral-900 line-clamp-2 min-h-[40px] leading-snug">
                                    {item.title}
                                  </h3>
                                  <div className="flex flex-wrap gap-2 mt-3">
                                    {item.pdf && (
                                      <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100 uppercase tracking-tighter shadow-sm">
                                        PDF Reader
                                      </span>
                                    )}
                                    {item.url && (
                                      <span className="text-[9px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-lg border border-red-100 uppercase tracking-tighter shadow-sm">
                                        Video Lesson
                                      </span>
                                    )}
                                    {item.day && (
                                      <span className="text-[9px] font-bold text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-lg border border-neutral-200 uppercase tracking-tighter shadow-sm">
                                        {item.day}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {filteredItems.length === 0 && (
                            <div className="col-span-full py-40 text-center">
                              <div className="w-20 h-20 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-dashed border-neutral-200">
                                <IconSearch
                                  size={28}
                                  className="text-neutral-300"
                                />
                              </div>
                              <p className="text-sm text-neutral-400 font-medium">
                                No matching items found in this collection.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Collection Modal */}
      {isCatModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[300] p-4 sm:p-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-[32px] w-full max-w-lg shadow-2xl border border-neutral-100 flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 md:p-8 border-b border-neutral-100 flex justify-between items-center bg-white flex-shrink-0">
              <div>
                <h2 className="text-2xl font-medium text-neutral-900 tracking-tight">
                  {activeTab === "article"
                    ? editCatId
                      ? "Category Settings"
                      : "Build New Category"
                    : editCatId
                      ? "Collection Settings"
                      : "Build New Collection"}
                </h2>
                <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest mt-1">
                  {activeTab === "article"
                    ? "Configure article categories"
                    : "Configure asset grouping"}
                </p>
              </div>
              <button
                onClick={() => setCatModalOpen(false)}
                className="w-10 h-10 bg-neutral-50 rounded-2xl flex items-center justify-center text-neutral-400 hover:text-neutral-900 transition-all active:scale-95"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form
              onSubmit={saveCategory}
              className="flex flex-col flex-grow overflow-hidden"
            >
              {/* Scrollable body */}
              <div className="flex-grow overflow-y-auto p-6 md:p-8 space-y-6">
                {activeTab === "all" && !editCatId && (
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Group Type
                    </label>
                    <div className="flex gap-2 mt-2">
                      {["video", "book", "magazine"].map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setCatForm({ ...catForm, type: t })}
                          className={`flex-1 py-3 rounded-2xl text-[10px] font-bold uppercase tracking-wider transition-all border ${catForm.type === t ? "bg-neutral-900 text-white border-neutral-900 shadow-md" : "bg-neutral-50 text-neutral-500 border-neutral-100 hover:border-neutral-300"}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                    Display Title
                  </label>
                  <input
                    required
                    type="text"
                    value={catForm.title}
                    onChange={(e) =>
                      setCatForm({ ...catForm, title: e.target.value })
                    }
                    className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 outline-none text-sm focus:border-neutral-900 transition-all font-medium placeholder:text-neutral-300"
                    placeholder="e.g. Hadith Studies"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Short Description
                    </label>
                    <input
                      type="text"
                      value={catForm.subtitle}
                      onChange={(e) =>
                        setCatForm({ ...catForm, subtitle: e.target.value })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none"
                      placeholder="e.g. 2026 Collection"
                    />
                  </div>
                </div>

                {(activeTab === "video" || catForm.type === "video") && (
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      External Playlist Link (YouTube)
                    </label>
                    <input
                      type="text"
                      value={catForm.mainUrl}
                      onChange={(e) =>
                        setCatForm({ ...catForm, mainUrl: e.target.value })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none"
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                )}

                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                    Visual Cover Path
                  </label>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={catForm.coverImage}
                        onChange={(e) =>
                          setCatForm({ ...catForm, coverImage: e.target.value })
                        }
                        className="flex-1 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none"
                        placeholder="Enter path or upload..."
                      />
                      <label className="p-3 bg-pumpkinToast-500 text-white rounded-2xl cursor-pointer hover:bg-neutral-800 transition-all shadow-md active:scale-95 flex items-center justify-center">
                        <IconUpload size={20} />
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleUpload(e, "coverImage", "cat")}
                        />
                      </label>
                    </div>
                    {catForm.coverImage && (
                      <div className="w-full h-32 rounded-3xl bg-neutral-100 overflow-hidden border border-neutral-200 relative group">
                        <img
                          src={formatFileUrl(catForm.coverImage)}
                          alt="Preview"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-black/10"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-neutral-100 flex justify-end gap-3 bg-white flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setCatModalOpen(false)}
                  className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-xs font-bold rounded-2xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  disabled={isSaving}
                  type="submit"
                  className="px-8 py-3 bg-pumpkinToast-500 hover:bg-neutral-800 text-white text-xs font-bold rounded-2xl uppercase tracking-[0.2em] shadow-lg shadow-pumpkinToast-500/20 transition-all active:scale-98 disabled:opacity-50 cursor-pointer"
                >
                  {isSaving
                    ? "Syncing..."
                    : editCatId
                      ? "Commit Changes"
                      : "Establish Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Item Modal */}
      {isItemModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[300] p-4 sm:p-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-[32px] w-full max-w-xl shadow-2xl border border-neutral-100 flex flex-col min-h-[80vh] max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 md:p-8 border-b border-neutral-100 flex justify-between items-center bg-white flex-shrink-0">
              <div>
                <h2 className="text-2xl font-medium text-neutral-900 tracking-tight">
                  {editItemId ? "Refine Resource" : "Inject New Resource"}
                </h2>
                <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest mt-1">
                  Resource Meta Data
                </p>
              </div>
              <button
                onClick={() => setItemModalOpen(false)}
                className="w-10 h-10 bg-neutral-50 rounded-2xl flex items-center justify-center text-neutral-400 hover:text-neutral-900 transition-all active:scale-95"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form
              onSubmit={saveItem}
              className="flex flex-col flex-grow overflow-hidden"
            >
              {/* Scrollable body */}
              <div className="flex-grow overflow-y-auto p-6 md:p-8 space-y-6">
                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                    Resource Title
                  </label>
                  <input
                    required
                    type="text"
                    value={itemForm.title}
                    onChange={(e) =>
                      setItemForm({ ...itemForm, title: e.target.value })
                    }
                    className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 outline-none text-sm focus:border-neutral-900 transition-all font-medium placeholder:text-neutral-300"
                    placeholder="Proper name of the asset..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {activeTab === "video" ? (
                    <>
                      <div>
                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                          Index / Day
                        </label>
                        <input
                          type="text"
                          value={itemForm.day}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, day: e.target.value })
                          }
                          className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs outline-none focus:border-neutral-900"
                          placeholder="e.g. Day 01"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                          YouTube Index
                        </label>
                        <input
                          type="text"
                          value={itemForm.url}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, url: e.target.value })
                          }
                          className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs outline-none focus:border-neutral-900"
                          placeholder="Full link or ID"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                          Meta Label
                        </label>
                        <input
                          type="text"
                          value={itemForm.month}
                          onChange={(e) =>
                            setItemForm({ ...itemForm, month: e.target.value })
                          }
                          className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 outline-none"
                          placeholder="e.g. Jan Issue / Edition"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                          Document (PDF)
                        </label>
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            value={itemForm.pdf}
                            onChange={(e) =>
                              setItemForm({ ...itemForm, pdf: e.target.value })
                            }
                            className="flex-1 px-4 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-[10px] focus:border-neutral-900 outline-none"
                            placeholder="Cloud path..."
                          />
                          <label className="p-3 bg-neutral-50 text-neutral-900 rounded-2xl cursor-pointer hover:bg-neutral-100 border border-neutral-200 transition-all flex items-center shadow-sm">
                            <IconUpload size={18} />
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => handleUpload(e, "pdf", "item")}
                            />
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                    Thumbnail Source
                  </label>
                  <div className="space-y-4">
                    <div className="flex gap-3 mt-1">
                      <input
                        type="text"
                        value={itemForm.coverImage}
                        onChange={(e) =>
                          setItemForm({
                            ...itemForm,
                            coverImage: e.target.value,
                          })
                        }
                        className="flex-1 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs outline-none focus:border-neutral-900"
                        placeholder="Leave empty for auto-generated..."
                      />
                      <label className="p-3 bg-pumpkinToast-500 text-white rounded-2xl cursor-pointer hover:bg-pumpkinToast-600 transition-all shadow-md active:scale-95 flex items-center justify-center">
                        <IconUpload size={18} />
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) =>
                            handleUpload(e, "coverImage", "item")
                          }
                        />
                      </label>
                    </div>
                    {itemForm.coverImage && (
                      <div className="w-full h-40 rounded-3xl bg-neutral-100 overflow-hidden border border-neutral-200 relative group">
                        <img
                          src={formatFileUrl(itemForm.coverImage)}
                          alt="Preview"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-neutral-100 flex justify-end gap-3 bg-white flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setItemModalOpen(false)}
                  className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-xs font-bold rounded-2xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  disabled={isSaving}
                  type="submit"
                  className="px-8 py-3 bg-pumpkinToast-500 hover:bg-neutral-800 text-white text-xs font-bold rounded-2xl uppercase tracking-[0.2em] shadow-lg shadow-pumpkinToast-500/20 transition-all active:scale-98 disabled:opacity-50 cursor-pointer"
                >
                  {isSaving
                    ? "Syncing Asset..."
                    : editItemId
                      ? "Publish Updates"
                      : "Commit Resource"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Article Modal */}
      {isArticleModalOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[300] p-4 sm:p-6 animate-in fade-in duration-300">
          <div className="bg-white rounded-[32px] w-full max-w-4xl shadow-2xl border border-neutral-100 flex flex-col min-h-[80vh] max-h-[95vh] overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 md:p-8 border-b border-neutral-100 flex justify-between items-center bg-white flex-shrink-0">
              <div>
                <h2 className="text-2xl font-medium text-neutral-900 tracking-tight">
                  {editArticleId ? "Edit Article" : "Write New Article"}
                </h2>
                <p className="text-[10px] text-neutral-400 font-bold uppercase tracking-widest mt-1">
                  Article Composer
                </p>
              </div>
              <button
                onClick={() => setArticleModalOpen(false)}
                className="w-10 h-10 bg-neutral-50 rounded-2xl flex items-center justify-center text-neutral-400 hover:text-neutral-900 transition-all active:scale-95"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form
              onSubmit={saveArticle}
              className="flex flex-col flex-grow overflow-hidden"
            >
              {/* Scrollable body */}
              <div className="flex-grow overflow-y-auto p-6 md:p-8 space-y-6">
                {/* Row 1: Title & Slug */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Article Title
                    </label>
                    <input
                      required
                      type="text"
                      value={articleForm.title}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          slug:
                            articleForm.slug ||
                            e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "-")
                              .replace(/(^-|-$)+/g, ""),
                          title: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-medium placeholder:text-neutral-300"
                      placeholder="Enter Malayalam or English title..."
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      URL Slug (Unique ID)
                    </label>
                    <input
                      type="text"
                      value={articleForm.slug}
                      onChange={(e) =>
                        setArticleForm({ ...articleForm, slug: e.target.value })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-medium placeholder:text-neutral-300"
                      placeholder="e.g. aswahul-kutubi-bada-kitabillah"
                    />
                  </div>
                </div>

                {/* Row 2: Arabic Calligraphy & Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Arabic Subtitle / Calligraphy
                    </label>
                    <input
                      type="text"
                      value={articleForm.arabicTitle}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          arabicTitle: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none text-right font-medium placeholder:text-neutral-300"
                      placeholder="أصح الكتب بعد كتاب الله"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Category
                    </label>
                    <select
                      required
                      value={articleForm.category}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          category: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-semibold text-neutral-700 cursor-pointer"
                    >
                      {categories.map((c) => (
                        <option key={c._id || c.title} value={c.title}>
                          {c.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 3: Author Name & Author Role */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Author Name
                    </label>
                    <input
                      required
                      type="text"
                      value={articleForm.author}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          author: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-medium placeholder:text-neutral-300"
                      placeholder="e.g. റഈസുൽ ഉലമ സുലൈമാൻ ഉസ്താദ്"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Author Role / Title
                    </label>
                    <input
                      type="text"
                      value={articleForm.authorRole}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          authorRole: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-medium placeholder:text-neutral-300"
                      placeholder="e.g. Ra'isul Ulama"
                    />
                  </div>
                </div>

                {/* Row 3b: Author Profile Image */}
                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                    Author Profile Image
                  </label>
                  <div className="flex gap-3 mt-2">
                    <input
                      type="text"
                      value={articleForm.authorImage}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          authorImage: e.target.value,
                        })
                      }
                      className="flex-1 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 focus:bg-white outline-none font-medium placeholder:text-neutral-300"
                      placeholder="Author profile image URL or upload..."
                    />
                    <label className="p-3 bg-pumpkinToast-500 text-white rounded-2xl cursor-pointer hover:bg-pumpkinToast-500/80 transition-all flex items-center shadow-md active:scale-95 justify-center">
                      <IconUpload size={18} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleUpload(e, "authorImage", "article")}
                      />
                    </label>
                  </div>
                  {articleForm.authorImage && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-neutral-200 flex-shrink-0">
                        <img
                          src={formatFileUrl(articleForm.authorImage)}
                          alt="Author preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-xs text-neutral-400 font-medium">Author profile image preview</span>
                    </div>
                  )}
                </div>

                {/* Row 4: Read Time & Excerpt */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Estimated Reading Time
                    </label>
                    <input
                      type="text"
                      value={articleForm.readTime}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          readTime: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-medium"
                      placeholder="e.g. 5 min read"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Brief Excerpt
                    </label>
                    <input
                      type="text"
                      value={articleForm.excerpt}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          excerpt: e.target.value,
                        })
                      }
                      className="w-full mt-2 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-sm focus:border-neutral-900 focus:bg-white outline-none font-medium placeholder:text-neutral-300"
                      placeholder="Summary of the article to show on card..."
                    />
                  </div>
                </div>

                {/* Cover Image Upload */}
                <div>
                  <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                    Featured Cover Image
                  </label>
                  <div className="flex gap-3 mt-2">
                    <input
                      type="text"
                      value={articleForm.image}
                      onChange={(e) =>
                        setArticleForm({
                          ...articleForm,
                          image: e.target.value,
                        })
                      }
                      className="flex-1 px-5 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 text-xs focus:border-neutral-900 focus:bg-white outline-none font-medium"
                      placeholder="Image URL or upload a file..."
                    />
                    <label className="p-3 bg-pumpkinToast-500 text-white rounded-2xl cursor-pointer hover:bg-pumpkinToast-500/80 transition-all flex items-center shadow-md active:scale-95 justify-center">
                      <IconUpload size={18} />
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => handleUpload(e, "image", "article")}
                      />
                    </label>
                  </div>
                  {articleForm.image && (
                    <div className="mt-3 w-full h-40 rounded-3xl overflow-hidden border border-neutral-200 relative group">
                      <img
                        src={formatFileUrl(articleForm.image)}
                        alt="Preview"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                </div>

                {/* Dynamic Block Editor for Article Content */}
                <div className="pt-6 border-t border-neutral-100">
                  <div className="flex justify-between items-center mb-4">
                    <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">
                      Article Content Blocks
                    </label>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleAddArticleBlock("paragraph")}
                        className="px-3 py-1.5 bg-pumpkinToast-50 hover:bg-pumpkinToast-100 text-pumpkinToast-700 text-xs font-bold rounded-lg border border-pumpkinToast-100 cursor-pointer"
                      >
                        + Paragraph
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddArticleBlock("heading")}
                        className="px-3 py-1.5 bg-[#1f0e56]/5 hover:bg-[#1f0e56]/10 text-[#1f0e56] text-xs font-bold rounded-lg border border-[#1f0e56]/10 cursor-pointer"
                      >
                        + Heading
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddArticleBlock("quote")}
                        className="px-3 py-1.5 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 text-xs font-bold rounded-lg border border-neutral-200 cursor-pointer"
                      >
                        + Quote
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddArticleBlock("question")}
                        className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-100 cursor-pointer"
                      >
                        + Question
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddArticleBlock("image")}
                        className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg border border-emerald-100 cursor-pointer"
                      >
                        + Image
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {articleForm.content.map((block, idx) => (
                      <div
                        key={idx}
                        className="p-4 bg-neutral-50/50 rounded-2xl border border-neutral-200/60 flex items-start gap-3"
                      >
                        {/* Left: type badges */}
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <select
                            value={block.type}
                            onChange={(e) =>
                              handleArticleBlockTypeChange(idx, e.target.value)
                            }
                            className="px-2 py-1 bg-white border border-neutral-200 rounded-md text-[10px] font-bold uppercase tracking-wider text-gray-500 outline-none"
                          >
                            <option value="paragraph">Paragraph</option>
                            <option value="heading">Heading</option>
                            <option value="quote">Quote</option>
                            <option value="question">Question</option>
                            <option value="image">Image</option>
                          </select>

                          {/* Move Up/Down */}
                          <div className="flex justify-center gap-1">
                            <button
                              type="button"
                              disabled={idx === 0}
                              onClick={() => handleMoveArticleBlock(idx, "up")}
                              className="p-1 bg-white border border-neutral-100 rounded text-neutral-400 hover:text-neutral-900 disabled:opacity-30 cursor-pointer flex items-center justify-center"
                            >
                              <IconChevronUp size={12} />
                            </button>
                            <button
                              type="button"
                              disabled={idx === articleForm.content.length - 1}
                              onClick={() =>
                                handleMoveArticleBlock(idx, "down")
                              }
                              className="p-1 bg-white border border-neutral-100 rounded text-neutral-400 hover:text-neutral-900 disabled:opacity-30 cursor-pointer flex items-center justify-center"
                            >
                              <IconChevronDown size={12} />
                            </button>
                          </div>
                        </div>

                        {/* Input Area */}
                        {block.type === "image" ? (
                          <div className="flex-grow flex flex-col gap-3">
                            <div className="flex gap-2 items-center">
                              <input
                                type="text"
                                required
                                value={block.text}
                                onChange={(e) =>
                                  handleArticleBlockChange(idx, e.target.value)
                                }
                                className="flex-grow p-3 bg-white border border-neutral-200 rounded-xl text-sm focus:border-neutral-900 outline-none transition-all text-neutral-900 text-neutral-700"
                                placeholder="Paste block image URL/path or upload..."
                              />
                              <label className="flex items-center gap-1.5 px-4 py-3 bg-pumpkinToast-50 hover:bg-pumpkinToast-100 text-pumpkinToast-700 border border-pumpkinToast-200 text-xs font-bold rounded-xl cursor-pointer transition-all">
                                <IconUpload size={14} />
                                <span>Upload</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleUploadBlockImage(e, idx)}
                                />
                              </label>
                            </div>
                            {block.text && (
                              <div className="relative w-full max-w-[200px] h-[120px] rounded-xl overflow-hidden border border-neutral-200/80 bg-neutral-100/50 flex items-center justify-center p-1">
                                <img
                                  src={formatFileUrl(block.text)}
                                  alt="Block preview"
                                  className="w-full h-full object-contain rounded-lg"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <textarea
                            required
                            rows={block.type === "paragraph" ? 4 : 2}
                            value={block.text}
                            onChange={(e) =>
                              handleArticleBlockChange(idx, e.target.value)
                            }
                            className={`flex-grow p-3 bg-white border border-neutral-200 rounded-xl text-sm focus:border-neutral-900 outline-none transition-all text-neutral-900 ${
                              block.type === "heading"
                                ? "font-bold text-neutral-900"
                                : block.type === "quote"
                                  ? "italic text-neutral-800 bg-neutral-100/50"
                                  : block.type === "question"
                                    ? "text-[#1f0e56] font-semibold bg-indigo-50/20"
                                    : "text-neutral-700"
                            }`}
                            placeholder={
                              block.type === "heading"
                                ? "Enter section heading..."
                                : block.type === "quote"
                                  ? "Enter quote citation..."
                                  : block.type === "question"
                                    ? "Enter question..."
                                    : "Enter paragraph body text..."
                            }
                          />
                        )}

                        {/* Delete Block */}
                        <button
                          type="button"
                          onClick={() => handleRemoveArticleBlock(idx)}
                          className="p-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg border border-red-100 cursor-pointer flex-shrink-0"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-neutral-100 flex justify-end gap-3 bg-white flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setArticleModalOpen(false)}
                  className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-xs font-bold rounded-2xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  disabled={isSaving}
                  type="submit"
                  className="px-8 py-3 bg-pumpkinToast-500 hover:bg-neutral-800 text-white text-xs font-bold rounded-2xl uppercase tracking-[0.2em] shadow-lg shadow-pumpkinToast-500/20 transition-all active:scale-98 disabled:opacity-50 cursor-pointer"
                >
                  {isSaving
                    ? "Syncing Article..."
                    : editArticleId
                      ? "Publish Updates"
                      : "Commit Article"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-neutral-950/40 backdrop-blur-md flex items-center justify-center z-[400] p-6 animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] p-8 w-full max-w-sm shadow-2xl relative border border-white/20 text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
              <FiTrash2 size={24} />
            </div>
            <h3 className="text-xl font-medium text-neutral-900 mb-2">
              Confirm Deletion
            </h3>
            <p className="text-sm text-neutral-500 mb-8">{confirmModal.text}</p>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  setConfirmModal({ isOpen: false, text: "", onConfirm: null })
                }
                className="flex-1 py-3 bg-neutral-100 text-neutral-600 rounded-2xl font-medium hover:bg-neutral-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="flex-1 py-3 bg-red-500 text-white rounded-2xl font-medium shadow-md shadow-red-500/20 hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
